/**
 * Building blocks the money dialogs (listing publish / renew, agent-support add-on) share, so the
 * two cannot drift apart: the lock-while-charging dialog shell, footer + button sizing, the card
 * line, the consent checkbox, the outcome alerts and the declined / in-progress / quote-error panels.
 * Every data-testid is `${testIdPrefix}-<name>`, exactly as the dialogs spelled them.
 */

import type { ComponentProps, ReactNode } from 'react';
import { Loader2 } from 'lucide-react';
import type { DefaultPaymentMethod } from '@resida/api-types/billing';
import { formatCardLabel } from '../../api/billing';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { CardSetupFlow, PaymentAlert, PAYMENT_DIALOG_CLASS } from './CardSetupFlow';

/** BR-91 / BR-102: never invite a second charge while an earlier attempt is being confirmed. */
const UNRECONCILED_COPY =
  "We're confirming an earlier payment attempt for this — please try again shortly or contact support.";

/** Full width (and 44px tall) on mobile, content-sized from md. */
export const CHARGE_BUTTON_CLASS = 'w-full md:w-auto min-h-[44px]';

/** Button row: stacked with the primary on top below md, right-aligned in a row from md. */
export function ChargeDialogFooter({ children, testId }: { children: ReactNode; testId?: string }) {
  return (
    <div className="flex flex-col-reverse gap-3 md:flex-row md:justify-end" data-testid={testId}>
      {children}
    </div>
  );
}

/** The secondary (outline) button of a footer: Cancel / Close / Not now. */
export function ChargeCancelButton({
  testId,
  label,
  onClick,
  disabled,
}: {
  testId: string;
  label: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <Button
      type="button"
      variant="outline"
      className={CHARGE_BUTTON_CLASS}
      data-testid={testId}
      onClick={onClick}
      disabled={disabled}
    >
      {label}
    </Button>
  );
}

/** The primary button of a footer, with the shared "Charging…" / "Publishing…" busy state. */
export function ChargeActionButton({
  testId,
  busy,
  busyLabel,
  disabled,
  onClick,
  children,
}: {
  testId: string;
  busy: boolean;
  busyLabel: string;
  disabled: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <Button type="button" className={CHARGE_BUTTON_CLASS} data-testid={testId} onClick={onClick} disabled={disabled}>
      {busy ? (
        <>
          <Loader2 className="animate-spin" aria-hidden="true" /> {busyLabel}
        </>
      ) : (
        children
      )}
    </Button>
  );
}

/**
 * Dialog surface that cannot be dismissed (X, Escape, outside click) while a charge is in flight.
 * `isChargeInFlight` reads the flow's ref so the guard is never a render behind.
 */
export function ChargeDialogShell({
  open,
  onOpenChange,
  charging,
  isChargeInFlight,
  testId,
  title,
  description,
  children,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  charging: boolean;
  isChargeInFlight: () => boolean;
  testId: string;
  title: string;
  description: string | null;
  children: ReactNode;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={`${PAYMENT_DIALOG_CLASS}${charging ? ' [&>button.absolute]:hidden' : ''}`}
        data-testid={testId}
        aria-describedby={undefined}
        onInteractOutside={(e) => {
          if (isChargeInFlight()) e.preventDefault();
        }}
        onEscapeKeyDown={(e) => {
          if (isChargeInFlight()) e.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description ? <DialogDescription>{description}</DialogDescription> : null}
        </DialogHeader>
        {children}
      </DialogContent>
    </Dialog>
  );
}

/** "Charged to Visa •••• 4242" (or another lead-in) + the "Use a different card" link. */
export function ChargeCardLine({
  testIdPrefix,
  card,
  charging,
  changeCardTestId,
  onChangeCard,
  leadIn = 'Charged to',
}: {
  testIdPrefix: string;
  card: DefaultPaymentMethod;
  charging: boolean;
  changeCardTestId: string;
  onChangeCard: () => void;
  /** Words before the card: "Charged to" where a charge is shown, "Pay with" before one is made. */
  leadIn?: string;
}) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-x-3 text-sm">
      <span className="text-foreground" data-testid={`${testIdPrefix}-card-line`}>
        {leadIn} <strong className="whitespace-nowrap">{formatCardLabel(card)}</strong>
      </span>
      <Button
        type="button"
        variant="link"
        className="min-h-[44px] px-0"
        data-testid={changeCardTestId}
        disabled={charging}
        onClick={onChangeCard}
      >
        Use a different card
      </Button>
    </div>
  );
}

/** The fee-consent checkbox + its copy. */
export function ChargeConsent({
  testId,
  checked,
  disabled,
  onChange,
  children,
}: {
  testId: string;
  checked: boolean;
  disabled: boolean;
  onChange: (checked: boolean) => void;
  children: ReactNode;
}) {
  return (
    <label className="flex items-start gap-2.5 text-sm leading-5 cursor-pointer min-h-[44px]">
      <input
        type="checkbox"
        data-testid={testId}
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 h-5 w-5 shrink-0 rounded border-input accent-primary"
      />
      <span>{children}</span>
    </label>
  );
}

/** "Charging Visa •••• 4242… Keep this window open." */
export function ChargingStatus({ testIdPrefix, card }: { testIdPrefix: string; card: DefaultPaymentMethod }) {
  return (
    <p role="status" className="m-0 text-sm text-muted-foreground" data-testid={`${testIdPrefix}-charging-status`}>
      Charging {formatCardLabel(card)}… Keep this window open.
    </p>
  );
}

/** The inline alerts of an attempt that ended without a definitive answer, or with a plain error. */
export function ChargeOutcomeAlerts({
  testIdPrefix,
  outcome,
}: {
  testIdPrefix: string;
  /** Any dialog outcome: only the kinds below render here, the dialog renders its own. */
  outcome: { kind: string; message?: string } | null;
}) {
  if (outcome?.kind === 'unknown_outcome') {
    return (
      <PaymentAlert tone="warning" testId={`${testIdPrefix}-unknown-outcome`}>
        We couldn&apos;t confirm whether your card was charged. Try again — the same request can&apos;t charge you
        twice.
      </PaymentAlert>
    );
  }
  if (outcome?.kind === 'unreconciled') {
    return (
      <PaymentAlert tone="warning" testId={`${testIdPrefix}-unreconciled`}>
        {UNRECONCILED_COPY}
      </PaymentAlert>
    );
  }
  if (outcome?.kind === 'error') {
    return (
      <PaymentAlert tone="destructive" testId={`${testIdPrefix}-error`}>
        {outcome.message}
      </PaymentAlert>
    );
  }
  return null;
}

/** Quote failed to load: message + Close / Retry. */
export function ChargeQuoteErrorPanel({
  testIdPrefix,
  message,
  onClose,
  onRetry,
}: {
  testIdPrefix: string;
  message: string;
  onClose: () => void;
  onRetry: () => void;
}) {
  return (
    <div className="flex flex-col gap-3">
      <PaymentAlert tone="destructive" testId={`${testIdPrefix}-quote-error`}>
        {message}
      </PaymentAlert>
      <ChargeDialogFooter>
        <ChargeCancelButton testId={`${testIdPrefix}-cancel`} label="Close" onClick={onClose} />
        <Button
          type="button"
          className={CHARGE_BUTTON_CLASS}
          data-testid={`${testIdPrefix}-retry-quote`}
          onClick={onRetry}
        >
          Retry
        </Button>
      </ChargeDialogFooter>
    </div>
  );
}

/** The card was declined: reason, what is unchanged, Cancel / Use a different card. */
export function ChargeDeclinedPanel({
  testIdPrefix,
  reason,
  unchangedNote,
  onClose,
  onUseDifferentCard,
}: {
  testIdPrefix: string;
  reason: string;
  /** What Nothing-was-charged is followed by, e.g. "agent support is still off." */
  unchangedNote: ReactNode;
  onClose: () => void;
  onUseDifferentCard: () => void;
}) {
  return (
    <div className="flex flex-col gap-4">
      <PaymentAlert tone="destructive" testId={`${testIdPrefix}-declined`}>
        <p className="m-0 font-medium">Your card was declined ({reason}).</p>
        <p className="m-0">Nothing was charged and {unchangedNote}</p>
      </PaymentAlert>
      <ChargeDialogFooter>
        <ChargeCancelButton testId={`${testIdPrefix}-cancel`} label="Cancel" onClick={onClose} />
        <Button
          type="button"
          className={CHARGE_BUTTON_CLASS}
          data-testid={`${testIdPrefix}-use-different-card`}
          onClick={onUseDifferentCard}
        >
          Use a different card
        </Button>
      </ChargeDialogFooter>
    </div>
  );
}

/** Another tab is already charging this: wait + Refresh, never charged twice. */
export function ChargeInProgressPanel({
  testIdPrefix,
  closeTestId,
  onClose,
  children,
}: {
  testIdPrefix: string;
  closeTestId: string;
  onClose: () => void;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-4">
      <PaymentAlert tone="warning" testId={`${testIdPrefix}-in-progress`}>
        {children}
      </PaymentAlert>
      <ChargeDialogFooter>
        <ChargeCancelButton testId={closeTestId} label="Close" onClick={onClose} />
        <Button
          type="button"
          className={CHARGE_BUTTON_CLASS}
          data-testid={`${testIdPrefix}-refresh`}
          onClick={() => window.location.reload()}
        >
          Refresh
        </Button>
      </ChargeDialogFooter>
    </div>
  );
}

type CardCaptureHandlers = Pick<ComponentProps<typeof CardSetupFlow>, 'requestSetupIntent' | 'onConfirmed' | 'onCancel'>;

/** Card capture step of a money dialog (one form: billing address + card; nothing is charged). */
export function ChargeCardCapture({
  testIdPrefix,
  summary,
  disableAddressAutocomplete,
  handlers,
}: {
  testIdPrefix: string;
  summary: ReactNode;
  disableAddressAutocomplete?: boolean;
  handlers: CardCaptureHandlers;
}) {
  return (
    <CardSetupFlow
      testIdPrefix={`${testIdPrefix}-card`}
      disableAddressAutocomplete={disableAddressAutocomplete}
      {...handlers}
      summary={summary}
      submitLabel="Save card & continue"
      singleStep
      addressHeading="Billing address"
    />
  );
}
